import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import styled from 'styled-components/native'

import { useIsMounted } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/useIsMounted'
import {
  PROVIDER_TAB_TITLE_MSG,
  TALK_WITH_PROVIDER_TAB,
  WellnessCheckInDomain,
} from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/constants'
import { WellnessCheckInResultsDomainProps } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/types'
import { getDomainDisplayName } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/utils'

import { Subhead } from '../../atoms/subhead/Subhead'
import { DotsPagination } from '../../molecules/dotsPagination/DotsPagination'
import { WellnessCheckInResultsDomainCard } from '../../molecules/wellnessCheckInResultsDomainCard/WellnessCheckInResultsDomainCard'
import { WellnessCheckInResultsDomainCardScore } from '../../molecules/wellnessCheckInResultsDomainCardScore/WellnessCheckInResultsDomainCardScore'
import { WellnessCheckInResultsDomainContent } from '../../molecules/wellnessCheckInResultsDomainContent/WellnessCheckInResultsDomainContent'
import { WellnessCheckInResultsProviderTabContent } from '../../molecules/wellnessCheckInResultsProviderTabContent/WellnessCheckInResultsProviderTabContent'
import { SubheadSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { getWellnessCheckInEssentialsContents } from '../../utils/wellnessCheckInResults/utils'

const DomainContainer = styled.View({
  margin: `80px auto 80px auto`,
  flexDirection: 'row',
  columnGap: '96px',
})

const DomainCardContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['16px'],
}))

const ContentContainer = styled.View({
  flex: 1,
  maxWidth: '464px',
})

const ContentHeaderContainer = styled.View<{ theme: ThemeType; isProviderTabActive: boolean }>(
  ({ theme, isProviderTabActive }) => ({
    marginBottom: isProviderTabActive ? theme.spacing['8px'] : theme.spacing['24px'],
  }),
)

const ContentTitleContainer = styled(Subhead)<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['8px'],
}))

const EssentialsContentContainer = styled.View<{ theme: ThemeType; isLastContent: boolean }>(
  ({ theme, isLastContent }) => ({
    marginBottom: isLastContent ? theme.spacing['0px'] : '36px',
  }),
)

export const WellnessCheckInResultsFullViewDomain: FunctionComponent<WellnessCheckInResultsDomainProps> = ({
  wellbeingResponse,
  tabKeys,
  domainScores,
  lowScoreDomains,
  onArticlePress,
  isPreview,
  onDomainPress,
  onLoad,
  onProviderTabCardPress,
  costMessagingProps,
}) => {
  if (Object.keys(wellbeingResponse).length === 0) {
    throw new Error('Empty wellbeing response')
  }
  const [activeTab, setActiveTab] = useState(tabKeys[0])
  const isProviderTabActive = activeTab === TALK_WITH_PROVIDER_TAB
  const essentialsContents = useMemo(() => {
    return (
      !isProviderTabActive &&
      getWellnessCheckInEssentialsContents(wellbeingResponse[activeTab as WellnessCheckInDomain].response)
    )
  }, [isProviderTabActive, activeTab, wellbeingResponse])

  const getIsMounted = useIsMounted()
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (getIsMounted() && !hasInitialized.current) {
      onLoad()
      hasInitialized.current = true
    }
  }, [getIsMounted, onLoad])

  const handleNavigateToIndex = (index: number) => {
    setActiveTab(tabKeys[index] as WellnessCheckInDomain)
    // Add a small delay to ensure content has rendered, and then scroll to 40px above the top of the domain content
    const timeout = setTimeout(() => {
      const domainElement = document.querySelector('[data-testid="WellnessCheckInResultsFullViewDomain"]')
      const offset = 40
      const elementRectTop = domainElement?.getBoundingClientRect().top ?? 0
      const targetScrollPosition = elementRectTop + window.scrollY - offset
      window.scrollTo({
        top: targetScrollPosition,
        behavior: 'smooth',
      })
    }, 100)
    return () => clearTimeout(timeout)
  }

  const activeDomainScore = domainScores[activeTab as WellnessCheckInDomain]

  return (
    <DomainContainer testID={tID(`WellnessCheckInResultsFullViewDomain`)}>
      <View>
        {tabKeys.length > 0 &&
          tabKeys.map((domain, index) => {
            return (
              <DomainCardContainer key={index}>
                <WellnessCheckInResultsDomainCard
                  title={
                    domain === TALK_WITH_PROVIDER_TAB
                      ? PROVIDER_TAB_TITLE_MSG
                      : getDomainDisplayName(domain as WellnessCheckInDomain)
                  }
                  score={domainScores[domain as WellnessCheckInDomain]}
                  onPress={() => {
                    onDomainPress && onDomainPress(domain as WellnessCheckInDomain)
                    setActiveTab(domain)
                  }}
                  isActive={activeTab === domain}
                  domain={domain}
                  isPreview={isPreview && index > 0}
                  key={`WellnessCheckInDomain-${index}`}
                />
              </DomainCardContainer>
            )
          })}
      </View>
      <ContentContainer>
        <ContentHeaderContainer isProviderTabActive={isProviderTabActive}>
          <ContentTitleContainer
            text={
              isProviderTabActive ? (
                <FormattedMessage {...PROVIDER_TAB_TITLE_MSG} />
              ) : (
                <FormattedMessage {...getDomainDisplayName(activeTab as WellnessCheckInDomain)} />
              )
            }
            size={SubheadSize.MEDIUM}
            testID={tID(`WellnessCheckInResultsFullViewDomain-domainTitle`)}
          />
          {!isProviderTabActive && activeDomainScore && (
            <WellnessCheckInResultsDomainCardScore score={activeDomainScore} />
          )}
        </ContentHeaderContainer>
        {isProviderTabActive ? (
          <WellnessCheckInResultsProviderTabContent
            lowScoreDomains={lowScoreDomains}
            costMessagingProps={costMessagingProps}
            onProviderTabCardPress={onProviderTabCardPress}
          />
        ) : (
          essentialsContents &&
          essentialsContents.length > 0 && (
            <>
              {essentialsContents.map((content, index) => {
                return (
                  <EssentialsContentContainer key={index} isLastContent={index === essentialsContents.length - 1}>
                    <WellnessCheckInResultsDomainContent
                      title={content.title}
                      body={content.body}
                      articleContents={content.articleContents}
                      key={activeTab}
                      onArticlePress={onArticlePress}
                      isLastContent={index === essentialsContents.length - 1}
                    />
                  </EssentialsContentContainer>
                )
              })}
            </>
          )
        )}
        {tabKeys.length > 1 && (
          <DotsPagination
            data={tabKeys}
            currentIndex={tabKeys.indexOf(activeTab)}
            onNavigateToIndex={handleNavigateToIndex}
            customStyles={{
              paginationContainer: {
                width: '464px',
                justifyContent: 'space-between',
              },
            }}
          />
        )}
      </ContentContainer>
    </DomainContainer>
  )
}
